import React, { useEffect, useMemo, useState } from 'react';
import { EntityType } from "@common/redux/types/common";

import styles from './BookingModal.module.scss';
import { Modal } from '../Modal/Modal';
import { IExcursion } from '@common/redux/types/excursion';
import { ITour } from '@common/redux/types/tour';
import { CalendarDatePicker } from '../CalendarDatePicker/CalendarDatePicker';
import classNames from 'classnames';
import moment from 'moment';
import { regionContent } from '@kursk/content/regionContent';
import { BookingModalInfo } from './BookingModalInfo/BookingModalInfo';
import { Counter } from '../Counter/Counter';
import { useForm, FormProvider } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { Input } from "@kursk/components/ui/Input/Input";
import { PhoneInput } from "@kursk/components/ui/PhoneInput/PhoneInput";
import { Button } from "@kursk/components/ui/Button/Button";
import { useDispatchThunk } from '@common/redux/dispatchThunk';
import { bookingSlice, bookingThunk } from '@common/redux/slices/booking.slice';
import { BookingModalSuccess } from './BookingModalSuccess/BookingModalSuccess';
import { useSelectSlice } from '@common/redux/selectors/useSelectSlice';

interface IProps {
  isOpen: boolean;
  onClose: () => void;
  entityType: EntityType;
  entity: IExcursion | ITour;
  defaultDate: number;
  defaultSeance?: number;
  scheduleGroups?: any[];
}


export function BookingModal({
  entityType,
  isOpen,
  onClose,
  entity,
  defaultDate,
  defaultSeance
}: IProps) {
  const [success, setSuccess] = useState(false);
  const { isSending } = useSelectSlice(bookingSlice);
  const [selectedDate, setSelectedDate] = useState(
    moment.tz(defaultDate, regionContent.timezone).startOf('day').valueOf()
  );
  const dispatchThunk = useDispatchThunk();
  const methods = useForm({
    mode: "onChange",
    resolver: yupResolver(Yup.object({
      userName: Yup.string().required('Это поле необходимо заполнить'),
      email: Yup.string().email('Указан некорректный адрес электронной почты').required('Необходимо ввести почту'),
      phone: Yup.string().required('Неверно указан номер телефона').min(13, 'Неверно указан номер телефона'),
      adultsNumber: Yup.number().when('childrenNumber', {
        is: 0,
        then: Yup.number().min(1, 'Необходимо ввести количество '),
      }),
      childrenNumber: Yup.number(),
      seanceId: Yup.number().nullable().required('Необходимо выбрать время'),
    })),
    defaultValues: {
      userName: '',
      email: '',
      phone: '',
      adultsNumber: 1,
      seanceId: defaultSeance || null,
      childrenNumber: 0,
    }
  });
  const handleDateChange = (value) => {
    methods.setValue('seanceId', null);
    setSelectedDate(value)
  }

  const data = useMemo(() => {
    if (entity) {
      return entity.schedules.reduce((acc, schedule) => {
        if (schedule.end <= Date.now()) {
          return acc
        }
        const date = moment.tz(schedule.start, regionContent.timezone).startOf('day').valueOf()
        const seanceData = {
          _id: schedule._id,
          start: schedule.start,
          end: schedule.end,
          ticketsInfo: schedule.ticketsInfo,
        };
        if (acc[date]) {
          acc[date].push(seanceData)
        } else {
          acc[date] = [seanceData]
        }
        return acc;
      }, {})
    } else {
      return {};
    }
  }, [entity])

  const onSubmit = async (values) => {
    const res = await dispatchThunk(bookingThunk({
      type: entityType,
      values: {
        ...values,
        entityId: entity._id,
        phone: values.phone.replace(/\s/g, "")
      }
    }))
    if (!res.error) {
      setSuccess(true)
    }
  }

  const keysActiveDays = Object.keys(data)
    .map(item => moment.tz(Number(item), regionContent.timezone).format('YYYY-MM-DD'))

  const [selectedSeance, childrenNumber] = methods.watch(['seanceId', 'childrenNumber']);

  useEffect(() => {
    methods.trigger('adultsNumber');
  }, [childrenNumber])

  const getSuccessDate = () => {
    const seance = data[selectedDate].find(item => item._id === selectedSeance);
    return moment.tz(seance.start, regionContent.timezone).format('DD MMMM, HH:mm')
  }
  return (
    <Modal isOpen={isOpen} onRequestClose={onClose}>
      {success ? <BookingModalSuccess name={entity.name} date={getSuccessDate()} onRequestClose={onClose} /> :
        <FormProvider {...methods}>
          <div className={styles.title}>Оставить заявку</div>
          <div className={styles.name}>{entity.name}</div>
          <BookingModalInfo entityType={entityType} entity={entity} />
          <div className={styles.section}>
            <div className={styles.section__title}>Дата и время</div>
            <div className={styles.section__content}>
              <div className={styles.formItem}>
                <label className={styles.label}>
                  Дата
                </label>
                <CalendarDatePicker
                  date={selectedDate}
                  minDate={moment().toDate()}
                  enabledDays={keysActiveDays}
                  onChangeDate={handleDateChange}
                  popperPlacement='bottom-start'
                />
              </div>
              <div className={styles.formItem}>
                <label className={styles.label}>
                  Время
                </label>
                <div>
                  <div className={styles.timesWrap}>
                    {selectedDate && data[selectedDate]?.map((seance) => (
                      <div
                        key={`${seance._id}`}
                        onClick={() => {
                          methods.setValue('seanceId', seance._id)
                          methods.trigger('seanceId')
                        }}
                        className={classNames(
                          styles.timeItem,
                          {
                            [styles.isActive]: selectedSeance === seance._id
                          }
                        )}>
                        {moment.tz(seance.start, regionContent.timezone).format('HH:mm')}
                      </div>
                    ))}
                  </div>
                  {methods.formState.errors.seanceId &&
                    <div className={styles.errorText}>{methods.formState.errors.seanceId.message}</div>
                  }
                </div>
              </div>
            </div>
          </div>
          <div className={styles.section}>
            <div className={styles.section__title}>Количество посетителей</div>
            <div className={styles.section__content}>
              <div>
                <div className={styles.counterRow}>
                  <div className={styles.formItem}>
                    <label className={styles.label}>
                      Взрослые
                    </label>
                    <Counter name='adultsNumber' control={methods.control} />
                  </div>
                  <div className={styles.formItem}>
                    <label className={styles.label}>
                      Дети
                    </label>
                    <Counter name='childrenNumber' control={methods.control} />
                  </div>
                </div>
                {methods.formState.errors.adultsNumber &&
                  <div className={styles.errorText}>{methods.formState.errors.adultsNumber.message}</div>
                }
              </div>
            </div>
          </div>
          <div className={styles.section}>
            <div className={styles.section__title}>Данные для связи</div>
            <div className={styles.section__content}>
              <div className={styles.formItem}>
                <label className={styles.label}>
                  Имя
                </label>
                <Input
                  {...methods.register("userName")}
                  errors={methods.formState.errors}
                />
              </div>
              <div className={styles.formItem}>
                <label className={styles.label}>
                  Email
                </label>
                <Input
                  {...methods.register("email")}
                  errors={methods.formState.errors}
                />
              </div>
              <div className={styles.formItem}>
                <label className={styles.label}>
                  Номер телефона
                </label>
                <PhoneInput
                  errors={methods.formState.errors}
                  control={methods.control}
                  register={methods.register}
                />
              </div>
            </div>
          </div>
          <div className={styles.buttonWrapper}>
            <Button
              text="Отправить"
              onClick={() => {
                methods.handleSubmit(onSubmit)();
              }}
              disabled={isSending}
            />
          </div>
          <div className={styles.agreementText}>
            Продажу билетов на {entityType === 'excursions' ? 'экскурсию': 'тур'} осуществляют партнёры портала.<br/>
            Нажимая кнопку «Отправить», вы направляете заявку соответствующему партнёру на
            приобретение билета на {entityType === 'excursions' ? 'экскурсию': 'тур'}, а также вы соглашаетесь с <a
              href={regionContent.agreementLink}
              target='_blank'
              rel="noreferrer"
            >
              условиями пользовательского соглашения
            </a>.
          </div>
        </FormProvider>
      }
    </Modal>
  );
}
